"use client";

import React from "react";
import SectionHeader from "./section-header";
import { blogCategories, blogPosts } from "@/lib/data";
import Image from "next/image";

/**
 * Blog Component
 *
 * Renders a categorized blog section containing a horizontal scrollable filter
 * and a responsive grid of blog posts. Features an empty state if no posts
 * are available in the selected category.
 *
 * @returns {JSX.Element} The rendered blog section component.
 */
const Blog = () => {
  /**
    * Tracks the currently active category filter.
   * Defaults to "All" to show every available post initially.
   */
  const [activeBlogTab, setActiveBlogTab] = React.useState("All");
  
  /**
   * Blog posts state, defaults to static data from lib but updates dynamically.
   */
  const [posts, setPosts] = React.useState<any[]>(blogPosts);

  /**
   * Fetch live WordPress posts on component mount.
   */
  React.useEffect(() => {
    const fetchWordPressPosts = async () => {
      try {
        const res = await fetch("https://blog.brimmhq.com/wp-json/wp/v2/posts?_embed&per_page=100", {
          // Bypass aggressive cache during development
          next: { revalidate: 60 } 
        });
        
        if (!res.ok) throw new Error("Failed to fetch");
        const wpPosts = await res.json();
        
        const formattedPosts = wpPosts.map((wp: any) => {
          // 1. Extract Category
          const terms = (wp._embedded?.['wp:term'] || []).flat();
          const categoryTerm = terms.find((t: any) => t.taxonomy === 'category');
          const categoryName = categoryTerm ? categoryTerm.name : "Uncategorized";

          // 2. Extract Author (with support for ACF fields)
          let authorName = "BrimmHQ Team";
          let authorRole = "Contributor";
          let authorAvatar = "/images/hero-section/hero-bg-default.webp"; 

          const embeddedAuthor = wp._embedded?.author?.[0];
          const acf = wp.acf || {};
          
          // Check ACF plugin fields first, then fallback to default WP User Profile
          if (acf.author_name) {
            authorName = acf.author_name;
          } else if (embeddedAuthor) {
            const title = embeddedAuthor.name_title ? `${embeddedAuthor.name_title} ` : "";
            authorName = `${title}${embeddedAuthor.name}`;
          }

          if (acf.author_title) authorRole = acf.author_title;
          else if (embeddedAuthor?.author_role) authorRole = embeddedAuthor.author_role;

          if (acf.author_image) {
             authorAvatar = typeof acf.author_image === "string" ? acf.author_image : (acf.author_image.url || authorAvatar);
          } else if (embeddedAuthor?.avatar_urls) {
             authorAvatar = embeddedAuthor.avatar_urls['96'] || authorAvatar;
          }

          // 3. Extract Featured Image
          let featuredImage = "/images/hero-section/hero-bg-default.webp";
          if (wp._embedded?.['wp:featuredmedia']?.[0]?.source_url) {
            featuredImage = wp._embedded['wp:featuredmedia'][0].source_url;
          }

          // Security: Safely decode strings and remove HTML using the browser's native DOMParser
          // This entirely prevents XSS vulnerabilities natively when fetching from a CMS
          const parseHtmlText = (htmlString: string) => {
            if (typeof window === 'undefined') return htmlString.replace(/<[^>]*>?/gm, ''); // Fallback
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlString, "text/html");
            return doc.body.textContent || "";
          };

          const safeTitle = parseHtmlText(wp.title.rendered || "");
          const safeExcerpt = parseHtmlText(wp.excerpt?.rendered || "");

          // 5. Format Date
          const formattedDate = new Date(wp.date).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          });

          return {
            id: wp.id,
            image: featuredImage,
            date: formattedDate,
            title: safeTitle,
            description: safeExcerpt,
            category: categoryName,
            link: wp.link, // The live URL to the blog post securely fetched
            author: {
              name: authorName,
              role: authorRole,
              avatar: authorAvatar
            }
          };
        });

        if (formattedPosts.length > 0) {
          setPosts(formattedPosts);
        }
      } catch (error) {
        console.error("Failed to connect to WordPress APIs, using fallback data.");
      }
    };

    fetchWordPressPosts();
  }, []);

  /**
   * Memoized array of blog posts filtered by the currently active tab.
   * Optimizes rendering by preventing unnecessary recalculations when re-rendering.
   */
  const filteredPosts = React.useMemo(() => {
    return posts.filter(
      (post) => activeBlogTab === "All" || post.category === activeBlogTab,
    );
  }, [activeBlogTab, posts]);

  /**
   * Updates the selected category tab when a user clicks on a category pill.
   *
   * @param {string} category - The label of the selected category.
   */
  const updateActiveBlogTab = (category: string) => {
    setActiveBlogTab(category);
  };

  return (
    /* Blog Section Container */
    <section
      className="2xl:container 2xl:mx-auto 2xl:px-4 px-0 md:px-4"
      aria-label="Blog Section"
      id="blog"
    >
      <div className="bg-surface-white pt-9.5 xl:pt-13.5 pb-8 xl:pb-12 px-4 md:px-7.5 lg:px-12 xl:px-21 space-y-4 xl:space-y-6">
        {/* Section Header */}
        <div className="space-y-4 lg:space-y-6 xl:space-y-12">
          <SectionHeader title="Blog" leftAlign={true} />
          <h2 className="capitalize text-[26px] lg:text-[32px] xl:text-[46px] leading-11 lg:leading-14.5 xl:leading-19.5 font-semibold text-surface-black tracking-normal">
            Brimm Hq Blog
          </h2>
        </div>

        <div className="space-y-5 lg:space-y-6 xl:space-y-8 text-sm xl:text-base">
          {/* Blog Categories Filter */}
          <div
            className="flex items-center overflow-x-auto snap-x snap-mandatory gap-x-2.5 lg:gap-x-4 xl:gap-x-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            aria-label="Blog Categories"
            role="group"
          >
            {blogCategories.map((category) => (
              <div
                key={category.id}
                onClick={() => updateActiveBlogTab(category.label)}
                className={`snap-start px-2.5 xl:px-4.5 py-2 xl:py-3 rounded-[10px] inline-flex items-center shrink-0 whitespace-nowrap gap-x-1.5 xl:gap-x-2 cursor-pointer ${activeBlogTab !== category.label ? `${category.color}` : `${category.dotColor} text-surface-white`}`}
                aria-label={`Category: ${category.label}`}
              >
                <span
                  className={`rounded-full size-2.5 lg:size-2.75 xl:size-3.75 leading-6 font-medium ${activeBlogTab !== category.label ? `${category.dotColor}` : `bg-surface-light  `}`}
                  aria-hidden="true"
                />
                {category.label}
              </div>
            ))}
          </div>

          {/* Blog Posts Grid */}
          {filteredPosts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[489.67px] xl:h-[617.17px] text-center rounded-[20px] bg-surface-white w-full border border-surface-light/50 drop-shadow-[0_0_24px_rgba(226,226,226,0.8)]">
              <svg
                className="w-16 h-16 text-surface/20 mb-5 stroke-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-lg xl:text-xl font-semibold text-surface">
                No posts found
              </p>
              <p className="text-sm md:text-base mt-2 text-surface/70 w-[80%] mx-auto md:w-full">
                We haven't published any posts under this category yet. Check
                back soon!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 md:gap-3 xl:gap-5">
              {filteredPosts.map((post) => {
                const category = blogCategories.find(
                  (cat) => cat.label === post.category,
                );
                return (
                  /* Individual Blog Post Card wrapped in a link to jump to WordPress */
                  <a
                    key={post.id}
                    href={post.link || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="outline outline-surface-light/88 rounded-[20px] bg-surface-white drop-shadow-[0_0_24px_rgba(226,226,226,0.8)] transition-transform hover:-translate-y-1 block"
                    aria-label={`Read blog post: ${post.title}`}
                  >
                    <article>
                      {/* Blog Post Image */}
                      <div className="relative w-full h-48 xl:h-63 overflow-hidden rounded-t-[20px]">
                        <Image
                          src={post.image}
                          alt={`Image for ${post.title}`}
                          fill
                          className="size-full object-center object-cover"
                        />
                      </div>

                      {/* Blog Post Content */}
                      <div className="px-4 pt-3.5 xl:pt-4.5 pb-4.25 xl:pb-6.25 text-surface space-y-2.75 md:space-y-2.5 xl:space-y-4">
                        <p className="">{post.date}</p>
                        <h2 className="text-[15.5px] md:text-base xl:text-[19px] font-semibold leading-5.75 md:leading-6.5 xl:leading-7 md:h-12 xl:h-[56px] line-clamp-2 text-ellipsis overflow-hidden">
                          {post.title}
                        </h2>

                        <p className="leading-6 md:leading-6.5 xl:leading-7 line-clamp-3 text-ellipsis text-sm lg:text-sm xl:text-base">
                          {post.description}
                        </p>

                        {/* Blog Post Author Details */}
                        <div className="space-y-1.5 xl:space-y-3">
                          <div className="flex items-center gap-x-2 justify-start">
                            <div className="relative rounded-full size-11 md:size-10 xl:size-12.5">
                              <Image
                                src={post.author.avatar}
                                alt={`Avatar of ${post.author.name}`}
                                fill
                                className="size-full object-center object-cover rounded-full"
                              />
                            </div>
                            <div className="space-y-1 md:space-y-0.5 xl:space-y-1">
                              <p className="font-semibold">{post.author.name}</p>
                              <span>{post.author.role}</span>
                            </div>
                          </div>

                          {/* Blog Post Category Tag */}
                          <div className="border-t-[0.5px] border-surface/50 pt-2.75 md:pt-2">
                            <p
                              className={`w-fit font-medium py-0.5 xl:py-1 px-2.5 xl:px-4.5 rounded-[20px] text-surface leading-7.5 ${category?.dotColor || 'bg-surface-light border border-surface/10'}`}
                              aria-label={`Category: ${post.category}`}
                            >
                              {post.category}
                            </p>
                          </div>
                        </div>
                      </div>
                    </article>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Blog;
